import type { FC, ReactNode } from 'react'
import type { ProductSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tilt } from '@/components/generals/tilt'
import { ProductEmblem } from '@/components/products/product-emblem'
import { isOptimizableImage } from '@/constants/media'

interface ProductCardProps {
    product: ProductSummary
    headingLevel?: 'h2' | 'h3'
    className?: string
}

export const ProductCard: FC<ProductCardProps> = ({
    product,
    headingLevel = 'h3',
    className
}): ReactNode => {
    const Heading = headingLevel
    const href = `/products/${product.slug}`

    return (
        <Tilt className='h-full rounded-[1.75rem]'>
            <article
                className={cn(
                    'group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-sand bg-card transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_28px_60px_-34px_rgba(15,42,30,0.55)]',
                    className
                )}
            >
                <div className='relative aspect-[4/3] overflow-hidden'>
                    <Link href={href} tabIndex={-1} aria-hidden='true' className='block size-full'>
                        {product.image ? (
                            <Image
                                src={product.image}
                                alt=''
                                fill
                                sizes='(min-width: 1280px) 28vw, (min-width: 768px) 45vw, 92vw'
                                unoptimized={!isOptimizableImage(product.image)}
                                className='object-cover transition-transform duration-700 group-hover:scale-105'
                            />
                        ) : (
                            <ProductEmblem
                                categorySlug={product.category_slug}
                                className='transition-transform duration-700 group-hover:scale-[1.03]'
                            />
                        )}
                    </Link>

                    {product.category_name && product.category_id && (
                        <span className='absolute start-4 top-4 rounded-full border border-gold/30 bg-forest-950/80 px-3 py-1 text-xs font-medium text-gold-light backdrop-blur'>
                            <span data-mdlfy-cms={`categories:${product.category_id}:name`}>
                                {product.category_name}
                            </span>
                        </span>
                    )}
                </div>

                <div className='flex flex-1 flex-col p-6'>
                    <Heading className='text-[1.65rem] font-bold leading-snug text-forest-900'>
                        <Link href={href} className='transition-colors hover:text-forest-600'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                {product.name}
                            </span>
                        </Link>
                    </Heading>

                    {product.scientific_name && (
                        <p className='mt-1 font-heading text-base italic text-gold-deep'>
                            <span dir='ltr' data-mdlfy-cms={`products:${product.id}:scientific_name`}>
                                {product.scientific_name}
                            </span>
                        </p>
                    )}

                    {product.summary && (
                        <p
                            data-mdlfy-cms={`products:${product.id}:summary`}
                            className='mt-3 line-clamp-3 text-sm leading-7 text-muted-foreground'
                        >
                            {product.summary}
                        </p>
                    )}

                    <div className='mt-auto flex items-center justify-between gap-4 border-t border-sand pt-4'>
                        {product.origin ? (
                            <span className='inline-flex min-w-0 items-center gap-1.5 text-xs text-forest-600'>
                                <MapPin aria-hidden='true' className='size-3.5 shrink-0 text-gold-deep' />
                                <span data-mdlfy-cms={`products:${product.id}:origin`} className='truncate'>
                                    {product.origin}
                                </span>
                            </span>
                        ) : <span />}

                        <Link
                            href={href}
                            aria-label={`جزئیات ${product.name}`}
                            className='inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-forest-700 transition-colors hover:text-gold-deep'
                        >
                            جزئیات
                            <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-1' />
                        </Link>
                    </div>
                </div>
            </article>
        </Tilt>
    )
}
